import React from 'react';
import { Avatar, Text, Box } from '@mantine/core';
import { Stack, Player } from '../types/models';

interface QueueVisualizationProps {
  queue: Stack[];
  activeStackId?: string;
  onStackClick?: (stack: Stack) => void;
  isCurrentUser?: boolean;
  players: Player[];
}

const QueueVisualization: React.FC<QueueVisualizationProps> = ({
  queue,
  activeStackId,
  onStackClick,
  isCurrentUser = false,
  players
}) => {
  // Helper to get origin player for a stack
  const getOriginPlayer = (stack: Stack): Player | undefined => {
    return players.find(p => p.id === stack.originPlayerId);
  };

  // If queue is empty, show empty state
  if (queue.length === 0) {
    return null;
  }

  return (
    <Box ta="center" mt="xs">
      <Avatar.Group spacing="xs">
        {queue.map((stack, index) => {
          const originPlayer = getOriginPlayer(stack);
          const isActive = stack.id === activeStackId;
          const isTopStack = index === 0;
          const isClickable = isCurrentUser && isTopStack;
          
          if (!originPlayer) return null;
          
          return (
            <Avatar
              key={stack.id}
              src={originPlayer.avatarUrl}
              size="md"
              radius="xl"
              style={{
                border: isActive ? '3px solid #0da388' : '2px solid #fff',
                cursor: isClickable ? 'pointer' : 'default',
                opacity: isTopStack ? 1 : 0.7,
                transform: isActive ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.2s ease',
                boxShadow: isActive 
                  ? '0 0 0 2px rgba(13, 163, 136, 0.2)' 
                  : '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: queue.length - index, // Stack properly
              }}
              onClick={() => isClickable && onStackClick?.(stack)}
              title={`${originPlayer.name}'s stack${isTopStack && isCurrentUser ? ' (click to start)' : ''}`}
            >
              {!originPlayer.avatarUrl && originPlayer.name.charAt(0).toUpperCase()}
            </Avatar>
          );
        })}
      </Avatar.Group>
      
      {/* Optional: Show queue size for current user */}
      {isCurrentUser && queue.length > 1 && (
        <Text size="xs" c="dimmed" mt={4}>
          {queue.length} in queue
        </Text>
      )}
    </Box>
  );
};

export default QueueVisualization;